import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { MailQuestion, Library, Menu, Moon, ShoppingCart, Sun, User, X } from "lucide-react";
import { useCart } from "../context/CartContext";

export function Navbar() {
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("dl-theme") || "dark"; } catch { return "dark"; }
  });
  const location = useLocation();
  const navigate = useNavigate();

  const goFilter = (key) => {
    setMobile(false);
    const apply = () => {
      window.dispatchEvent(new CustomEvent("set-category-filter", { detail: key }));
      document.getElementById("katalog")?.scrollIntoView({ behavior: "smooth" });
    };
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(apply, 400);
    } else {
      apply();
    }
  };

  const goSection = (id) => {
    setMobile(false);
    const apply = () => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(apply, 400);
    } else {
      apply();
    }
  };

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try { localStorage.setItem("dl-theme", theme); } catch {}
  }, [theme]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobile(false), [location.pathname]);

  return (
    <header className={`fixed inset-x-0 top-0 z-40 transition-[background-color,box-shadow] duration-300 ${scrolled ? "bg-slate-950/85 shadow-lg shadow-cyan-500/5 backdrop-blur-2xl border-b border-cyan-500/15" : "bg-transparent"}`}>
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/30">
            <Library className="h-5 w-5 text-white" />
          </div>
          <span className="font-display text-xl font-extrabold tracking-tight">
            <span className="gradient-text">DigiLib</span><span className="text-slate-400">.store</span>
          </span>
        </Link>

        <div className="hidden items-center gap-8 md:flex">
          <Link to="/" className="text-sm font-medium text-slate-300 transition-colors hover:text-cyan-300">Strona główna</Link>
          <button onClick={() => goFilter("format:ebook")} className="text-sm font-medium text-slate-300 transition-colors hover:text-cyan-300">Ebooki</button>
          <button onClick={() => goFilter("format:audiobook")} className="text-sm font-medium text-slate-300 transition-colors hover:text-cyan-300">Audiobooki</button>
          <button onClick={() => goSection("aplikacje")} className="text-sm font-medium text-slate-300 transition-colors hover:text-fuchsia-300">Aplikacje</button>
          <button onClick={() => goSection("gry")} data-testid="nav-games" className="text-sm font-bold text-amber-300 transition-colors hover:text-amber-200">Gry</button>
          <Link to="/blog" className="text-sm font-medium text-slate-300 transition-colors hover:text-cyan-300">Blog</Link>
          <Link to="/konto" data-testid="nav-account" className="flex items-center gap-1.5 text-sm font-medium text-slate-300 transition-colors hover:text-cyan-300"><User className="h-4 w-4" /> Konto</Link>
          <button onClick={() => document.getElementById("quiz")?.scrollIntoView({ behavior: "smooth" })}
            className="text-sm font-medium text-slate-300 transition-colors hover:text-cyan-300">Quiz</button>
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="theme-toggle"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="rounded-full glass-panel p-2.5 text-slate-200 transition-colors hover:border-cyan-400/40 hover:text-cyan-300"
            aria-label={theme === "dark" ? "Przełącz na jasny motyw" : "Przełącz na ciemny motyw"}
            title={theme === "dark" ? "Jasny motyw" : "Ciemny motyw"}
          >
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
          <button
            data-testid="nav-book-request"
            onClick={() => window.dispatchEvent(new Event("open-book-request"))}
            className="hidden items-center gap-1.5 rounded-full border border-fuchsia-400/40 bg-gradient-to-r from-fuchsia-500/20 to-cyan-500/20 px-4 py-2 text-xs font-bold text-fuchsia-300 transition-[transform,border-color] duration-200 hover:scale-105 hover:border-fuchsia-300/70 md:flex"
          >
            <MailQuestion className="h-3.5 w-3.5" /> Nie ma Twojej książki?
          </button>
          <button
            data-testid="nav-cart-button"
            onClick={() => setOpen(true)}
            className="relative rounded-full glass-panel p-2.5 text-slate-200 transition-colors hover:border-cyan-400/40 hover:text-cyan-300"
            aria-label="Koszyk"
          >
            <ShoppingCart className="h-5 w-5" />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-r from-cyan-400 to-teal-400 text-[10px] font-bold text-slate-950">
                {count}
              </span>
            )}
          </button>
          <button className="rounded-full glass-panel p-2.5 text-slate-200 md:hidden" onClick={() => setMobile(!mobile)} aria-label="Menu">
            {mobile ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>
      {mobile && (
        <div className="border-t border-cyan-500/10 bg-slate-950/95 px-4 py-4 backdrop-blur-2xl md:hidden">
          <div className="flex flex-col gap-3">
            <Link to="/" className="text-sm font-medium text-slate-200">Strona główna</Link>
            <Link to="/blog" className="text-sm font-medium text-slate-200">Blog</Link>
            <Link to="/konto" data-testid="nav-account-mobile" className="text-sm font-medium text-slate-200">Moje konto</Link>
            <button onClick={() => goFilter("format:ebook")} className="text-left text-sm font-medium text-slate-200">Ebooki</button>
            <button onClick={() => goFilter("format:audiobook")} className="text-left text-sm font-medium text-slate-200">Audiobooki</button>
            <button onClick={() => goSection("aplikacje")} className="text-left text-sm font-medium text-fuchsia-300">Aplikacje interaktywne</button>
            <button onClick={() => goSection("gry")} data-testid="nav-games-mobile" className="text-left text-sm font-bold text-amber-300">Gry edukacyjne</button>
            <button onClick={() => { setMobile(false); document.getElementById("quiz")?.scrollIntoView({ behavior: "smooth" }); }} className="text-left text-sm font-medium text-slate-200">Quiz</button>
            <button onClick={() => { setMobile(false); window.dispatchEvent(new Event("open-book-request")); }} data-testid="nav-book-request-mobile" className="text-left text-sm font-bold text-fuchsia-300">Nie ma Twojej książki?</button>
          </div>
        </div>
      )}
    </header>
  );
}
